/*
  ICONTACTIC W-164 — pointer zoom inside the product gallery lightbox.

  Horizon ships DragZoomWrapper, which starts at 1.5x and only binds its handlers on a mobile breakpoint. On a
  desktop that left the lightbox opening magnified past the edges of the screen with no way to steer it. The CSS in
  icontactic-pdp-zoom.css opens it fitted instead; this adds the steering: click the photograph to magnify the
  point under the cursor, move to pan, click again to go back to fitted.

  Four things this had to work around:

  - Every <li> in the lightbox carries on:click="/close". Clicking the photograph closed the whole gallery: that is
    the theme's own behaviour, not a side effect of anything here, and it is why clicking to enlarge kept shutting
    the lightbox. The binding is removed from those items, so the click reaches the zoom instead. Closing stays on
    the X button and on Escape, both of which are bound elsewhere and are untouched.
  - zoom-dialog.js swaps the whole <img> node for a high-resolution copy the moment the dialog opens, so the image
    is looked up on every event. A cached reference points at the detached old node, whose box is zero, and the
    pointer maths silently produced nothing.
  - Once the picture is magnified its bounding box is the scaled one, so mapping the pointer through it walks off
    the edge of the photograph and the origin pins itself at 100%. offsetWidth and offsetHeight are the layout
    size and ignore the transform, so the pointer is mapped against those instead.
  - This lives in an asset rather than inline in the section because an inline script is part of the page HTML: a
    browser holding a cached copy of the page keeps running the old version of it while still picking up the
    current CSS from its own file, which is how the background looked fixed while the click behaviour did not.

  Only fine pointers get the zoom. A phone keeps the theme's own pinch and drag, which DragZoomWrapper writes as an
  inline style on the wrapper and which therefore still wins over the stylesheet. The close binding is removed on
  every device, because tapping the photograph to look closer should not dismiss the gallery there either.
*/
(function () {
  var FINE_POINTER = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var ZOOM = 2.6;

  function clamp(v) {
    if (isNaN(v)) return 50;
    return v < 0 ? 0 : v > 100 ? 100 : v;
  }

  /* The theme closes the gallery when the picture itself is clicked. Drop that one binding. */
  function releaseCloseOnMedia(wrapper) {
    var item = wrapper.closest('.product-media-container');
    if (item && item.getAttribute('on:click') === '/close') item.removeAttribute('on:click');
  }

  function bind(wrapper) {
    if (wrapper.dataset.ictZoomBound) return;
    wrapper.dataset.ictZoomBound = '1';

    releaseCloseOnMedia(wrapper);

    if (!FINE_POINTER) return;

    var on = false;

    /* Layout box of the picture: offsetWidth ignores the transform, so this stays correct while magnified. The
       image is centred in the wrapper, so its untransformed position follows from the wrapper's own box. */
    function imageBox() {
      var img = wrapper.querySelector('img');
      if (!img) return null;
      var iw = img.offsetWidth;
      var ih = img.offsetHeight;
      if (!iw || !ih) return null;
      var box = wrapper.getBoundingClientRect();
      return {
        left: box.left + (box.width - iw) / 2,
        top: box.top + (box.height - ih) / 2,
        width: iw,
        height: ih
      };
    }

    function isOnImage(event) {
      var b = imageBox();
      if (!b) return false;
      return (
        event.clientX >= b.left &&
        event.clientX <= b.left + b.width &&
        event.clientY >= b.top &&
        event.clientY <= b.top + b.height
      );
    }

    function follow(event) {
      var b = imageBox();
      if (!b) return;
      wrapper.style.setProperty('--ict-zoom-x', clamp(((event.clientX - b.left) / b.width) * 100) + '%');
      wrapper.style.setProperty('--ict-zoom-y', clamp(((event.clientY - b.top) / b.height) * 100) + '%');
    }

    function zoomOut() {
      on = false;
      wrapper.classList.remove('ict-zoom-on');
      wrapper.style.setProperty('--ict-zoom-scale', '1');
    }

    function zoomIn(event) {
      follow(event);
      on = true;
      wrapper.classList.add('ict-zoom-on');
      wrapper.style.setProperty('--ict-zoom-scale', String(ZOOM));
    }

    wrapper.addEventListener(
      'click',
      function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (on) zoomOut();
        else if (isOnImage(event)) zoomIn(event);
      },
      true
    );

    wrapper.addEventListener('pointermove', function (event) {
      if (on) follow(event);
    });

    wrapper.addEventListener('pointerleave', zoomOut);
  }

  function bindAll(root) {
    (root || document).querySelectorAll('zoom-dialog drag-zoom-wrapper').forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bindAll(); }, { once: true });
  } else {
    bindAll();
  }

  /* The gallery is re-rendered when a variant changes, so new wrappers are picked up as they appear. */
  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      for (var j = 0; j < records[i].addedNodes.length; j++) {
        var node = records[i].addedNodes[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('drag-zoom-wrapper')) bind(node);
        else if (node.querySelectorAll) bindAll(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
